import { VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse, Service } from '@genial/services';
import {
  InteractionChannelAuditRequest,
  ResponsesFundOrderCreateResponse,
} from '@genial/services/dist/models';
import React from 'react';

import { useSendingChannels } from '../../../../../../../../hooks/useSendingChannels';
import { useToastCreator } from '../../../../../../../../hooks/useToastCreator';
import { useFundsAssetController } from '../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/Funds';
import {
  orderResumePdfTemplate,
  parsePDFDataExport,
  setupPDF,
} from '../../../../../../../Resume/utils';
import { CardHeader } from '../../components/CardHeader';
import { ConfirmationButton } from '../../components/ConfirmationButton';
import { SimplifiedApplicationMissingTerm } from '../../components/SimplifiedApplicationMissingTerm';
import { TicketApplicationInProgress } from '../../components/TicketApplicationInProgress';
import { TicketApplicationSuccessDetails } from '../../components/TicketApplicationSuccessDetails';
import { defaultTicketApplicationBaseInfo } from '../../components/TicketApplicationSuccessDetails/constants';
import { TicketDetails } from '../../components/TicketDetails';
import {
  defaultErrorMessageApplication,
  screenName,
  screenNameSimplifiedFlow,
} from '../../constants';
import { CustomerApplicationModalProductProps } from '../../types';

import { FundInvestCard } from './components/FundInvestCard';
import { getFundsSuccessInfos } from './constants';

export interface FundsApplicationProps extends CustomerApplicationModalProductProps<'funds'> {
  onClose: () => void;
  handleFollowTicket: () => void;
}

export const FundsApplication: React.FC<FundsApplicationProps> = ({
  offer,
  onClose,
  customerId,
  productType,
  accountNumber,
  handleFollowTicket,
  isSimplifiedApplication,
  isSimplifiedApplicationFlagMatched,
  showSendingChannels,
  clientHasTerm,
  isClientTermLoading,
}) => {
  const { showToastFromError, showToast } = useToastCreator('fundsApplicationInCustomer');
  const [sendingOrder, setSendingOrder] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [successInfos, setSuccessInfos] = React.useState(defaultTicketApplicationBaseInfo);
  const [interactionChannelAudit, setInteractionChannelAudit] =
    React.useState<InteractionChannelAuditRequest | null>();
  const [ticketSuccessData, setTicketSuccessData] =
    React.useState<ResponsesFundOrderCreateResponse>();

  const offerController = useFundsAssetController(offer);
  const sendingChannelsController = useSendingChannels(customerId);

  const buttonDisabled =
    !!offerController.hasError ||
    (!sendingChannelsController.sendingChannelsSelected.length && !isSimplifiedApplication) ||
    interactionChannelAudit === undefined ||
    offerController.currentValue <= 0;

  const handleExportNewOrder = () => {
    if (!ticketSuccessData) {
      return;
    }
    const data = parsePDFDataExport(ticketSuccessData as any);
    setupPDF(orderResumePdfTemplate(data, 'Fund'), `ordem-${successInfos.orderHash}.pdf`);
  };

  const handleConfirmOrder = () => {
    setSendingOrder(true);
    Service.Fund.V1.postOrdersBuy({
      screenName: isSimplifiedApplication ? screenNameSimplifiedFlow : screenName,
      fundOrderCreateRequest: {
        accountNumber,
        orderAmount: offerController.currentValue as number,
        productId: offer.productIdNormalized,
        notificationChannels: isSimplifiedApplication
          ? null
          : sendingChannelsController.sendingChannelsSelected,
        orderName: 'Fundos',
        interactionChannelAudit: interactionChannelAudit!,
        requestedDateQuotation: undefined as any,
      },
    })
      .then(({ data }) => {
        setSuccess(true);
        setTicketSuccessData(data);
        setSuccessInfos(
          getFundsSuccessInfos(
            data.offer,
            data.order,
            showSendingChannels,
            isSimplifiedApplicationFlagMatched
          )
        );
      })
      .catch((error) => {
        if (instanceOfErrorListResponse(error as object)) {
          (error as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }

        showToast(...defaultErrorMessageApplication);
      })
      .finally(() => setSendingOrder(false));
  };

  if (success) {
    return (
      <TicketApplicationSuccessDetails
        {...successInfos}
        onClose={onClose}
        onFollow={handleFollowTicket}
        onExportPdf={handleExportNewOrder}
      />
    );
  }

  if (sendingOrder) {
    return <TicketApplicationInProgress />;
  }

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <CardHeader onClose={onClose} />

      <FundInvestCard offer={offer} offerController={offerController} />

      <SimplifiedApplicationMissingTerm
        isClientTermAvailable={clientHasTerm}
        isClientTermLoading={isClientTermLoading}
        isSimplifiedApplicationFlagMatched={isSimplifiedApplicationFlagMatched}
      />

      <TicketDetails
        productTypeId={productType}
        sendingChannelsController={sendingChannelsController}
        setInteractionChannelAudit={setInteractionChannelAudit}
        interactionChannelAudit={interactionChannelAudit}
        showSendingChannels={showSendingChannels}
        isSimplifiedApplicationFlagMatched={isSimplifiedApplicationFlagMatched}
      />

      <ConfirmationButton onConfirm={handleConfirmOrder} disabled={buttonDisabled} />
    </VStack>
  );
};
